import apiRequest from "../../utils/apiRequest";

/**
 * Services HTTP des domaines Drugs et DrugsStock (Phase 2.F).
 *
 * <p>Aligné sur le contrat back :
 * <ul>
 *   <li>POST /drugs → 201 + Long (id).</li>
 *   <li>PUT /drugs/{id} → Long (id).</li>
 *   <li>DELETE /drugs/{id} → void.</li>
 *   <li>GET /drugs/{id} → DrugsResponse.</li>
 *   <li>GET /drugs?search&page&size → Map paginée
 *       (PageMapper.toMap : content/totalElements/...). Remplace
 *       l'ancien POST /drugs/get-filter supprimé en Phase 14.</li>
 *   <li>POST /drugs-stock → 201 + Long (id).</li>
 *   <li>GET /drugs-stock/{id} → DrugsStockResponse.</li>
 *   <li>GET /drugs-stock?search&page&size → Map paginée. Remplace
 *       l'ancien POST /drugs-stock/get-filter.</li>
 * </ul>
 *
 * <p>Toutes les fonctions retournent l'enveloppe uniforme apiRequest :
 * {@code { ok, status, data, message?, error? }}.
 *
 * <p>Note de compat : les anciennes signatures acceptaient un argument
 * {@code token} en seconde position — conservé en paramètre optionnel mais
 * ignoré (apiRequest gère l'auth via le storage).
 */

/* ------------------------------------------------------------------ */
/* Drugs                                                               */
/* ------------------------------------------------------------------ */

export const createDrug = async (payload) => {
  // Compat : l'ancien composant injectait `action` dans le payload.
  const body = { ...(payload || {}) };
  delete body.action;
  return apiRequest.post("/drugs", body);
};

export const updateDrug = async (payload = {}) => {
  const id = payload?.id;
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  const body = { ...payload };
  delete body.action;
  delete body.id;
  return apiRequest.put(`/drugs/${id}`, body);
};

export const deleteDrug = async (payload) => {
  // Compat : accepte soit l'objet historique {id}, soit l'id directement.
  const id = typeof payload === "object" && payload !== null ? payload?.id : payload;
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.del(`/drugs/${id}`);
};

export const getDrugById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/drugs/${id}`);
};

/**
 * Catalogue paginé des médicaments — remplace l'ancien
 * {@code POST /drugs/get-filter}.
 *
 * @param {object} [params={}]
 * @param {string} [params.search='']
 * @param {number} [params.page=0]
 * @param {number} [params.size=20]
 */
export const getDrugs = async ({ search = "", page = 0, size = 20 } = {}) => {
  const qs = new URLSearchParams({ search, page: String(page), size: String(size) }).toString();
  return apiRequest.get(`/drugs?${qs}`);
};

/**
 * @deprecated alias rétro-compatible vers {@link getDrugs}. Aplatit la
 * réponse paginée en tableau plat dans {@code data} pour préserver l'API
 * des consommateurs (saga drugs, Pharmacy, Consultation/TabTwo) qui font
 * {@code drugs.map / .filter / .find}.
 */
export const getDrugsByFilter = async (payload = {}) => {
  const search =
    payload?.search ?? payload?.name ?? payload?.label ?? payload?.q ?? "";
  const result = await getDrugs({ search });
  if (!result?.ok) return result;
  return { ...result, data: result.data?.content ?? [] };
};

/**
 * Lookup léger {id, label} search-as-you-type pour les selects.
 *
 * <p>Règles serveur :
 * <ul>
 *   <li>{@code q} doit faire au moins 2 caractères ; sinon le serveur renvoie
 *       {@code []} (200, pas une 400).</li>
 *   <li>{@code limit} par défaut 20, plafonné serveur à 50.</li>
 *   <li>recherche par préfixe insensible casse/accents.</li>
 * </ul>
 *
 * @param {object} [params={}]
 * @param {string} [params.q='']
 * @param {number} [params.limit=20]
 * @returns enveloppe avec {@code data} = {@code LookupItem[]}.
 */
export const getDrugsLookup = async ({ q = "", limit = 20 } = {}) => {
  const qs = new URLSearchParams({ q, limit: String(limit) }).toString();
  return apiRequest.get(`/drugs/lookup?${qs}`);
};

/**
 * Lookup d'un médicament par id — utilisé pour précharger le label
 * affiché dans un select en édition.
 *
 * @returns enveloppe avec {@code data} = {@code LookupItem} ou 404.
 */
export const getDrugsLookupById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/drugs/lookup/${id}`);
};

/* ------------------------------------------------------------------ */
/* DrugsStock                                                          */
/* ------------------------------------------------------------------ */

export const createDrugStock = async (payload) => {
  const body = { ...(payload || {}) };
  delete body.action;
  return apiRequest.post("/drugs-stock", body);
};

export const getDrugStockById = async (id) => {
  if (id == null) {
    return { ok: false, status: 0, message: "id manquant", error: "BadRequest" };
  }
  return apiRequest.get(`/drugs-stock/${id}`);
};

/**
 * Catalogue paginé des mouvements de stock — remplace
 * {@code POST /drugs-stock/get-filter}.
 */
export const getDrugsStock = async ({ search = "", page = 0, size = 20 } = {}) => {
  const qs = new URLSearchParams({ search, page: String(page), size: String(size) }).toString();
  return apiRequest.get(`/drugs-stock?${qs}`);
};

/**
 * @deprecated alias rétro-compatible vers {@link getDrugsStock}. Aplatit la
 * réponse paginée en tableau plat dans {@code data} pour préserver l'API
 * des consommateurs (Pharmacy/TabTwo qui font
 * {@code drugsSuivie.filter(drug => drug?.drugs?.name…)}).
 */
export const getDrugsStockByFilter = async (payload = {}) => {
  const search =
    payload?.search ?? payload?.name ?? payload?.label ?? payload?.q ?? "";
  const result = await getDrugsStock({ search });
  if (!result?.ok) return result;
  return { ...result, data: result.data?.content ?? [] };
};
